import { Tooltip, Typography } from '@/components'
import { paths } from '@/routes/paths'
import { useGoToPath } from '@/utils'
import { ChevronRight } from 'lucide-react'
import { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TableCell, TitleCellContainer, UnreadDot } from './styles'

type TitleCellProps = {
  companyId: string
  claimId: string
  title: string
  isClickable: boolean
  unreadCount: number
}

const TitleCell = ({
  companyId,
  claimId,
  title,
  isClickable,
  unreadCount,
}: TitleCellProps) => {
  const { t } = useTranslation()
  const goToClaim = useGoToPath(paths.viewClaimWithKey({ companyId, claimId }))

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      goToClaim()
    }
  }

  const isUnread = unreadCount > 0
  const unreadLabel = t('claimsPage.table.unread', { count: unreadCount })

  const content = (
    <>
      {isUnread && (
        <UnreadDot role="img" aria-label={unreadLabel} title={unreadLabel} />
      )}
      <Tooltip content={title}>
        <Typography bold={isUnread} ellipsis variant="p2Body">
          {title}
        </Typography>
      </Tooltip>
    </>
  )

  if (!isClickable) {
    return (
      <TableCell>
        <TitleCellContainer>{content}</TitleCellContainer>
      </TableCell>
    )
  }

  return (
    <TableCell>
      <TitleCellContainer
        role="button"
        tabIndex={0}
        aria-label={t('claimsPage.table.viewClaim', { title })}
        onClick={goToClaim}
        onKeyDown={handleKeyDown}
      >
        {content}
        <ChevronRight size={20} />
      </TitleCellContainer>
    </TableCell>
  )
}

export default TitleCell
